/**
 * El horario de la agenda: por día o por grupos de días.
 *
 * POR QUÉ NO VALE UN «DE 9 A 20 DE LUNES A VIERNES»
 * ────────────────────────────────────────────────
 * Porque casi ningún negocio abre así. Un sábado corto, una tarde que se cierra,
 * un martes de mañana. Con un solo par de horas para todos los días, el operador
 * tiene dos salidas y las dos son malas: poner el horario más amplio —y el
 * asistente ofrecerá huecos que no existen— o el más estrecho, y perder reservas
 * reales.
 *
 * El horario NO vive en la ficha: vive en la agenda (FR-018). Esto solo lo
 * recoge para que el aprovisionamiento lo siembre allí.
 *
 * L-V Y S-D SEPARADOS, Y NO ES ESTÉTICA
 * ─────────────────────────────────────
 * Entre semana y fin de semana casi nunca comparten horario, así que agruparlos
 * en una fila de siete botones iguales invita a marcarlos todos y arreglarlo
 * después — que es como se acaba con un domingo de 9 a 20 que nadie quiso.
 */
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'

export interface HorarioDia {
    abierto: boolean
    /** Formato `HH:MM`. */
    abre: string
    cierra: string
}

export type HorarioSemanal = Record<number, HorarioDia>

/** Domingo es 0 en la agenda, pero la semana empieza en lunes para quien la lee. */
export const DIAS_LABORABLES = [
    { v: 1, corto: 'Lun', largo: 'Lunes' },
    { v: 2, corto: 'Mar', largo: 'Martes' },
    { v: 3, corto: 'Mié', largo: 'Miércoles' },
    { v: 4, corto: 'Jue', largo: 'Jueves' },
    { v: 5, corto: 'Vie', largo: 'Viernes' },
] as const

export const DIAS_FIN_DE_SEMANA = [
    { v: 6, corto: 'Sáb', largo: 'Sábado' },
    { v: 0, corto: 'Dom', largo: 'Domingo' },
] as const

export function horarioPorDefecto(): HorarioSemanal {
    const h: HorarioSemanal = {}
    for (const d of DIAS_LABORABLES) h[d.v] = { abierto: true, abre: '09:00', cierra: '20:00' }
    h[6] = { abierto: true, abre: '10:00', cierra: '14:00' }
    h[0] = { abierto: false, abre: '10:00', cierra: '14:00' }
    return h
}

function FilaDia({
    dia,
    valor,
    onChange,
}: {
    dia: { v: number; largo: string }
    valor: HorarioDia
    onChange: (h: HorarioDia) => void
}) {
    return (
        <div
            className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 transition-colors ${
                valor.abierto
                    ? 'border-neutral-700 bg-neutral-900'
                    : 'border-neutral-800 bg-neutral-900/40'
            }`}
        >
            <label className="flex w-24 shrink-0 items-center gap-2 text-sm">
                <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-700 bg-neutral-900"
                    checked={valor.abierto}
                    onChange={(e) => onChange({ ...valor, abierto: e.target.checked })}
                />
                <span className={valor.abierto ? 'text-neutral-100' : 'text-neutral-500'}>
                    {dia.largo}
                </span>
            </label>
            {valor.abierto ? (
                <div className="flex items-center gap-2">
                    <Input
                        type="time"
                        className="w-24 px-2 text-center"
                        value={valor.abre}
                        onChange={(e) => onChange({ ...valor, abre: e.target.value })}
                        aria-label={`${dia.largo}: abre`}
                    />
                    <span className="text-xs text-neutral-500">a</span>
                    <Input
                        type="time"
                        className="w-24 px-2 text-center"
                        value={valor.cierra}
                        onChange={(e) => onChange({ ...valor, cierra: e.target.value })}
                        aria-label={`${dia.largo}: cierra`}
                    />
                </div>
            ) : (
                <span className="text-xs text-neutral-600">cerrado</span>
            )}
        </div>
    )
}

function Grupo({
    titulo,
    dias,
    horario,
    onChange,
}: {
    titulo: string
    dias: readonly { v: number; largo: string }[]
    horario: HorarioSemanal
    onChange: (h: HorarioSemanal) => void
}) {
    /** Copiar el primero abierto al resto del grupo: el atajo que se usa siempre. */
    function igualarGrupo() {
        const patron = dias.map((d) => horario[d.v]).find((h) => h?.abierto)
        if (!patron) return
        const nuevo = { ...horario }
        for (const d of dias) nuevo[d.v] = { ...nuevo[d.v]!, abre: patron.abre, cierra: patron.cierra }
        onChange(nuevo)
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label>{titulo}</Label>
                <button
                    type="button"
                    className="text-xs text-neutral-400 underline hover:text-neutral-200"
                    onClick={igualarGrupo}
                >
                    Igualar todos a las horas del primero
                </button>
            </div>
            <div className="space-y-1.5">
                {dias.map((d) => (
                    <FilaDia
                        key={d.v}
                        dia={d}
                        valor={horario[d.v] ?? { abierto: false, abre: '09:00', cierra: '20:00' }}
                        onChange={(h) => onChange({ ...horario, [d.v]: h })}
                    />
                ))}
            </div>
        </div>
    )
}

export function HorarioSemana({
    horario,
    onChange,
}: {
    horario: HorarioSemanal
    onChange: (h: HorarioSemanal) => void
}) {
    return (
        // Ancho acotado y centrado: con siete filas de dos horas cada una, a
        // 1700px el bloque quedaba perdido en medio de la pantalla.
        <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
            <Grupo titulo="De lunes a viernes" dias={DIAS_LABORABLES} horario={horario} onChange={onChange} />
            <Grupo titulo="Fin de semana" dias={DIAS_FIN_DE_SEMANA} horario={horario} onChange={onChange} />
        </div>
    )
}

/** Los días abiertos, para el contrato viejo que aún los pide como lista. */
export function diasAbiertos(h: HorarioSemanal): number[] {
    return Object.entries(h)
        .filter(([, v]) => v.abierto)
        .map(([k]) => Number(k))
        .sort()
}

/**
 * La hora más temprana y la más tardía de la semana.
 *
 * El contrato viejo pide un solo par para todos los días. Se toma el sobre
 * abierto —la más temprana y la más tardía— porque es el único que no deja
 * fuera ninguna hora que el negocio sí abre. El detalle por día viaja aparte,
 * a la agenda, que es donde de verdad se calculan los huecos.
 */
export function sobreDeHoras(h: HorarioSemanal): { abre: number; cierra: number } {
    const abiertos = Object.values(h).filter((d) => d.abierto)
    if (abiertos.length === 0) return { abre: 9, cierra: 20 }
    const hora = (s: string) => Number(s.split(':')[0] ?? 0)
    return {
        abre: Math.min(...abiertos.map((d) => hora(d.abre))),
        cierra: Math.max(...abiertos.map((d) => hora(d.cierra))),
    }
}
